import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { isNotNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';
import { UserEntity } from 'Models/Entities';
import { store } from 'Models/Store';
import { appConfigStore } from 'Models/AppConfigStore';
import { userAccountKeys } from 'Modules/UserAccount/Hooks/userAccountKeys';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import Pagination from 'Views/Components/Pagination/Pagination';
import If from 'Views/Components/If/If';
import Icon from '../../Icon/Icon';
import { GiftVouchers } from './GiftVouchersInner';
import { AccountDetailsTabHeader } from '../AccountDetailsTabHeader';
import { Toggle } from '../../Toggle/Toggle';
import { getUsersGiftVouchers } from 'Services/Api/_HumanWritten/GiftCertificateService';
import PurchaseGiftVoucherModal from 'Views/Components/_HumanWritten/PurchaseGiftCertificate/PurchaseGiftVoucherModal';

interface AccountDetailsVouchersProps {
	user?: UserEntity;
}

function AccountDetailsVouchers({ user }: AccountDetailsVouchersProps) {
	const [searchParams] = useSearchParams();
	const queryClient = useQueryClient();
	const perPage = 4;
	const [pageNo, setPageNo] = useState<number>(0);
	// Held as "show active" rather than "show expired/redeemed" so the toggle reads the way round it
	// is labelled: on = Active, which is also the default. The endpoint still takes the inverse.
	const [showActive, setShowActive] = useState<boolean>(true);
	const [showPurchaseModal, setShowPurchaseModal] = useState(false);
	const [searchTerm, setSearchTerm] = useState(searchParams.get('search') ?? '');
	const [claimedVoucherId, setClaimedVoucherId] = useState(searchParams.get('claimed') ?? '');

	// staleTime 0 so every page/filter change refetches (matching the legacy fetch-per-change),
	// while keepPreviousData stops pagination flashing an empty list.
	const { data: giftCertificates } = useQuery({
		queryKey: userAccountKeys.giftVoucherPage(user?.id, { pageNo, showExpiredOrUsed: !showActive, searchTerm }),
		queryFn: () => getUsersGiftVouchers(user!.id, pageNo * 4, 4, !showActive, searchTerm || undefined),
		enabled: isNotNullOrUndefined(user),
		placeholderData: keepPreviousData,
		staleTime: 0,
	});

	const reload = () => {
		queryClient.invalidateQueries({ queryKey: userAccountKeys.giftVouchers(user?.id) });
	};

	// Clear the claimed query param after animation plays
	useEffect(() => {
		if (!claimedVoucherId) {
			return undefined;
		}
		const timer = setTimeout(() => {
			setClaimedVoucherId('');
			const newParams = new URLSearchParams(searchParams);
			newParams.delete('claimed');
			window.history.replaceState(
				{},
				'',
				`${window.location.pathname}${newParams.toString() ? `?${newParams}` : ''}`,
			);
		}, 3000);
		return () => clearTimeout(timer);
	}, [claimedVoucherId]);

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(e.target.value);
		setPageNo(0);
	};

	return (
		<>
			<div className="account-page-ferry-vouchers__header-container">
				<AccountDetailsTabHeader headerText="Ferry credits" />
				<div className="credit-voucher__manager-buttons">
					{store.isStaff && (
						<Toggle
							className="credit-voucher__toggle"
							id="credit-voucher-toggle"
							model={{ showActiveCredits: showActive }}
							modelProperty="showActiveCredits"
							labelVisible
							labelOn="Active"
							labelOff="Expired/Redeemed"
							onChecked={(event, checked) => {
								setShowActive(checked);
								setPageNo(0);
							}}
						/>
					)}
					<form onSubmit={e => e.preventDefault()}>
						<div className="search-box">
							<input
								placeholder="Search by code"
								className="fh-basic-search"
								autoComplete="off"
								value={searchTerm}
								onChange={handleSearchChange}
							/>
							<If condition={stringNotEmpty(searchTerm)}>
								<Button
									className="clear-search"
									onClick={e => {
										e.preventDefault();
										setSearchTerm('');
										setPageNo(0);
										const params = new URLSearchParams(searchParams);
										params.delete('search');
										window.history.replaceState(
											{},
											'',
											`${window.location.pathname}${params.toString() ? `?${params}` : ''}`,
										);
									}}
									colors={Colors.Black}
									display={Display.Text}
								>
									<Icon name="cross" />
								</Button>
							</If>
						</div>
					</form>
					{(store.isStaff || appConfigStore.voucherPurchaseEnabled) && (
						<Button
							className="add-ferry-voucher__button"
							onClick={() => setShowPurchaseModal(true)}
							icon={{ icon: 'add', iconPos: 'icon-left' }}
						>
							Buy credits
						</Button>
					)}
				</div>
			</div>
			<GiftVouchers
				reload={reload}
				listOfGiftCertificates={giftCertificates}
				claimedVoucherId={claimedVoucherId}
			/>
			<PurchaseGiftVoucherModal
				isOpen={showPurchaseModal}
				onClose={() => {
					setShowPurchaseModal(false);
					reload();
				}}
				prefillCustomerId={store.isStaff && user ? user.id : undefined}
				prefillCustomer={store.isStaff && user ? user : undefined}
				prefillCustomerName={
					// Names are non-nullable in the model but can be null in the database, which would
					// otherwise prefill the literal string "null null".
					store.isStaff && user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : undefined
				}
			/>
			<If condition={isNotNullOrUndefined(giftCertificates) && giftCertificates?.count > 4}>
				<div className="collection-component">
					<section className="collection__pagination new-style">
						<section className="collection__load">
							<Pagination
								perPage={perPage}
								pageNo={pageNo}
								totalRecords={giftCertificates?.count ?? 0}
								onPageChange={pageNum => {
									setPageNo(pageNum);
								}}
								showGoToPageBox={false}
								showFirstArrow={false}
								showLastArrow={false}
								leftArrowIcon="arrow-left"
								rightArrowIcon="arrow-right"
							/>
						</section>
					</section>
				</div>
			</If>
		</>
	);
}

export default AccountDetailsVouchers;
